import {
  AuroraEnv,
  AuroraEnvMapping,
  AuroraSubDomain,
  auroraStageDomains,
  auroraProdDomains,
} from './env.models';

const envMapping: AuroraEnvMapping = Object.freeze({
  app: AuroraEnv.PRODUCTION,
  kueskipay: AuroraEnv.PRODUCTION,
  localhost: AuroraEnv.LOCAL,
  prod: AuroraEnv.PRODUCTION,
  staging: AuroraEnv.STAGING,
  testing: AuroraEnv.STAGING,
});

export const isDevMode = () => process.env.NODE_ENV === 'development';
export const isProductionMode = () => process.env.NODE_ENV === 'production';

/**
 * Get current subdomain
 *
 * @returns {AuroraSubDomain} Current subdomain
 */
export const getSubDomain = (): AuroraSubDomain => (
  window?.location?.hostname?.split('.').shift() as AuroraSubDomain
) ?? 'localhost';

/**
 * Validate CDN Domains
 *
 * @returns {AuroraEnv} The identified domain
 */
export const validateCDNDomains = (): AuroraEnv => {
  const host = window?.location?.hostname;

  if (auroraStageDomains.includes(host)) return AuroraEnv.STAGING;
  if (auroraProdDomains.includes(host)) return AuroraEnv.PRODUCTION;
  return AuroraEnv.LOCAL;
};

/**
 * Get current environment
 *
 * @returns Current environment
 */
export const getEnv = (): AuroraEnv => envMapping[getSubDomain()] ?? validateCDNDomains();

export const isLocal = (): boolean => getEnv() === AuroraEnv.LOCAL;
export const isStaging = (): boolean => getEnv() === AuroraEnv.STAGING;
export const isProd = (): boolean => getEnv() === AuroraEnv.PRODUCTION;
export const ifProd = <A, B = null>(prodValue: A, nonProdValue: B = null as B): A | B => (
  isProd() ? prodValue : nonProdValue
);

/**
 * Adds current environment's sufix to a string ("_STAGING" or "_PROD")
 *
 * @param key
 * @returns Sufixed string
 */
export const addEnvSuffix = (key: string): string => `${key}_${getEnv()}`;
/**
 * Adds current environment's prefix to a string ("STAGING_" or "PROD_")
 *
 * @param key
 * @returns Sufixed string
 */
export const addEnvPrefix = (key: string): string => `${getEnv()}_${key}`;

/**
 * Gets variable value for current environment using the standardized name.
 *
 * In example, `getVariable('VAR')` on staging, will attempt to retrieve:
 *
 * 1. `process.env.STAGING_VAR` (variable with environment prefix)
 * 2. `process.env.VAR_STAGING` (variable with environment suffix)
 * 3. `process.env.VAR`  (variable with no suffix or prefix)
 *
 * @param variable
 * @returns variable value for current environment
 */
export const getVariable = (variable: string) => {
  const prefixed = addEnvPrefix(variable);
  const sufixed = addEnvSuffix(variable);

  return process.env[prefixed] ?? process.env[sufixed] ?? process.env[variable] ?? null;
};

export default {
  addEnvPrefix,
  addEnvSuffix,
  getEnv,
  getSubDomain,
  ifProd,
  isDevMode,
  isLocal,
  isProd,
  isProductionMode,
  isStaging,
};
